export type AccountStatus =
  | "pending"
  | "active"
  | "suspended"
  | "banned"
  | "anonymized";

export type ServiceAdminStatus = "draft" | "active" | "inactive" | "archived";

export type ReportTargetType =
  | "post"
  | "comment"
  | "profile"
  | "community"
  | "message";

export type ReportReason =
  | "spam"
  | "offensive"
  | "professionally_dangerous"
  | "personal_data"
  | "advertising"
  | "other";

export type ReportStatus = "open" | "reviewed" | "actioned" | "dismissed";

export type AdminDashboardStats = {
  community: {
    registeredUsers: number;
    activeUsers: number;
    pendingUsers: number;
    dau: number;
    wau: number;
    mau: number;
    postsToday: number;
    postsTotal: number;
    commentsToday: number;
    commentsTotal: number;
    reactionsTotal: number;
    communitiesTotal: number;
    communitiesActive: number;
    openReports: number;
  };
  business: {
    leadsTotal: number;
    leadsNew: number;
    leadsActive: number;
    offersTotal: number;
    offersAccepted: number;
    contracted: number;
    expectedValue: number;
    conversionRate: number;
    leadsByService: { name: string; count: number }[];
  };
};

export type AdminActivityPoint = {
  date: string;
  posts: number;
  comments: number;
  reactions: number;
  messages: number;
  registrations: number;
  total: number;
};

export type SystemRole = "admin" | "superadmin";

export type AdminNotificationBroadcast = {
  id: string;
  createdAt: string;
  actorName: string | null;
  notificationType: "system_announcement" | "system_message";
  title: string;
  body: string | null;
  targetPath: string;
  broadcast: boolean;
  recipientCount: number;
};

export type AdminUserRow = {
  id: string;
  fullName: string;
  email: string | null;
  occupation: string | null;
  city: string | null;
  accountStatus: AccountStatus;
  systemRole: string | null;
  lastSeenAt: string | null;
  createdAt: string;
  communityCount: number;
  requestCount: number;
};

export type AdminPostType =
  | "text"
  | "image"
  | "video"
  | "document"
  | "link"
  | "poll"
  | "event"
  | "question";

export type AdminPostAudience = "global" | "community" | "practice";

export type AdminPostAction =
  | "hide"
  | "unhide"
  | "pin"
  | "unpin"
  | "delete"
  | "restore";

export type AdminPostRow = {
  id: string;
  authorId: string;
  authorName: string;
  postType: AdminPostType;
  audience: AdminPostAudience;
  contentPreview: string;
  communityNames: string | null;
  practiceNames: string | null;
  isHidden: boolean;
  isPinned: boolean;
  deletedAt: string | null;
  commentCount: number;
  createdAt: string;
};

export const ADMIN_POST_TYPES: AdminPostType[] = [
  "text",
  "image",
  "video",
  "document",
  "link",
  "poll",
  "event",
  "question",
];

export const ADMIN_POST_TYPE_LABELS: Record<AdminPostType, string> = {
  text: "Szöveg",
  image: "Kép",
  video: "Videó",
  document: "Dokumentum",
  link: "Link",
  poll: "Szavazás",
  event: "Esemény",
  question: "Kérdés",
};

export const ADMIN_POST_AUDIENCE_LABELS: Record<AdminPostAudience, string> = {
  global: "Nyilvános",
  community: "Közösség",
  practice: "Praxis",
};

export type AdminCommunityRow = {
  id: string;
  name: string;
  slug: string;
  visibility: string;
  ownerName: string;
  memberCount: number;
  isFeatured: boolean;
  isActive: boolean;
  createdAt: string;
  suspendedAt: string | null;
  archivedAt: string | null;
};

export type ServiceCtaType =
  | "interest"
  | "quote"
  | "consultation"
  | "start"
  | "order";
export type ServicePricingModel = "custom_quote" | "fixed" | "free";
export type ServiceFulfillmentType = "none" | "chat" | "feed";
export type FeedAccessDuration =
  | "days"
  | "month_1"
  | "month_3"
  | "month_6"
  | "year_1"
  | "forever";

export type AdminServiceRow = {
  id: string;
  name: string;
  slug: string;
  categoryName: string;
  status: ServiceAdminStatus;
  publishedAt: string | null;
  leadCount: number;
  updatedAt: string;
  partnerName: string | null;
  isFeatured: boolean;
  sortOrder: number;
  ctaType: ServiceCtaType;
  pricingModel: ServicePricingModel;
  priceAmount: number | null;
  priceUnit: string | null;
};

export type AdminServiceCategoryOption = {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  sortOrder: number;
  isActive: boolean;
  serviceCount: number;
};

export type AdminServiceOptions = {
  categories: AdminServiceCategoryOption[];
};

export type AdminServiceDocument = {
  id?: string;
  title: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  storagePath: string;
};

export type AdminServiceDetail = {
  id: string;
  name: string;
  slug: string;
  categoryId: string;
  summary: string;
  description: string;
  iconName: string | null;
  iconColor: string | null;
  coverPath: string | null;
  coverUrl: string | null;
  ctaType: ServiceCtaType;
  ctaLabel: string;
  pricingModel: ServicePricingModel;
  priceAmount: number | null;
  priceUnit: string | null;
  currency: string;
  status: ServiceAdminStatus;
  sortOrder: number;
  isFeatured: boolean;
  audienceSummary: string | null;
  problemStatement: string | null;
  benefits: string[];
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  faq: { question: string; answer: string }[];
  providerName: string | null;
  documents: AdminServiceDocument[];
  fulfillmentType: ServiceFulfillmentType;
  specialistUserId: string | null;
  specialistName: string | null;
  feedCommunityId: string | null;
  feedCommunityName: string | null;
  feedAccessDuration: FeedAccessDuration | null;
  feedAccessDays: number | null;
};

export type AdminServiceUpsertInput = {
  id?: string;
  name: string;
  categoryId: string;
  summary: string;
  description: string;
  iconName: string | null;
  iconColor: string | null;
  coverPath: string | null;
  ctaType: ServiceCtaType;
  pricingModel: ServicePricingModel;
  priceAmount: number | null;
  priceUnit: string | null;
  status: ServiceAdminStatus;
  sortOrder: number;
  isFeatured: boolean;
  audienceSummary: string;
  problemStatement: string;
  benefits: string[];
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  faq: { question: string; answer: string }[];
  providerName: string;
  documents: AdminServiceDocument[];
  fulfillmentType: ServiceFulfillmentType;
  specialistUserId: string | null;
  feedCommunityId: string | null;
  feedAccessDuration: FeedAccessDuration | null;
  feedAccessDays: number | null;
};

export type AdminServiceCategoryUpsertInput = {
  id?: string;
  name: string;
  description: string;
  isActive: boolean;
};

export type AdminReportRow = {
  id: string;
  targetType: ReportTargetType;
  targetId: string;
  reason: ReportReason;
  details: string | null;
  status: ReportStatus;
  reporterName: string;
  targetPreview: string;
  createdAt: string;
  resolvedAt: string | null;
  resolutionAction: string | null;
};

export const ACCOUNT_STATUS_LABELS: Record<AccountStatus, string> = {
  pending: "Jóváhagyásra vár",
  active: "Aktív",
  suspended: "Felfüggesztett",
  banned: "Tiltott",
  anonymized: "Anonimizált",
};

export const SYSTEM_ROLE_LABELS: Record<SystemRole | "member", string> = {
  member: "Tag",
  admin: "Admin",
  superadmin: "Superadmin",
};

export const SERVICE_STATUS_LABELS: Record<ServiceAdminStatus, string> = {
  draft: "Piszkozat",
  active: "Aktív",
  inactive: "Inaktív",
  archived: "Archivált",
};

export const SERVICE_CTA_LABELS: Record<ServiceCtaType, string> = {
  interest: "Érdekel",
  quote: "Ajánlatot kérek",
  consultation: "Tanácsadást kérek",
  start: "Szolgáltatás indítása",
  order: "Megrendelem",
};

export const SERVICE_PRICING_LABELS: Record<ServicePricingModel, string> = {
  custom_quote: "Egyedi ajánlat",
  fixed: "Fix ár",
  free: "Ingyenes",
};

export const SERVICE_FULFILLMENT_LABELS: Record<ServiceFulfillmentType, string> =
  {
    none: "Nincs extra hozzáférés",
    chat: "Szakértői chat",
    feed: "Privát hírfolyam",
  };

export const FEED_ACCESS_DURATION_LABELS: Record<FeedAccessDuration, string> = {
  days: "X nap",
  month_1: "1 hónap",
  month_3: "3 hónap",
  month_6: "6 hónap",
  year_1: "1 év",
  forever: "Örökre",
};

export const FEED_ACCESS_DURATIONS: FeedAccessDuration[] = [
  "days",
  "month_1",
  "month_3",
  "month_6",
  "year_1",
  "forever",
];

export const REPORT_REASON_LABELS: Record<ReportReason, string> = {
  spam: "Spam",
  offensive: "Sértő tartalom",
  professionally_dangerous: "Szakmailag veszélyes",
  personal_data: "Személyes adat",
  advertising: "Reklám",
  other: "Egyéb",
};

export const REPORT_TARGET_LABELS: Record<ReportTargetType, string> = {
  post: "Bejegyzés",
  comment: "Hozzászólás",
  profile: "Profil",
  community: "Közösség",
  message: "Chatüzenet",
};

export const REPORT_STATUS_LABELS: Record<ReportStatus, string> = {
  open: "Nyitott",
  reviewed: "Átnézve",
  actioned: "Intézkedve",
  dismissed: "Elutasítva",
};

export type AdminConversationType = "direct" | "group" | "community";
export type AdminConversationKind =
  | AdminConversationType
  | "service";
export type AdminMessageType = "text" | "image" | "file" | "link" | "system";
export type AdminMessageAction = "delete" | "restore";

export type AdminConversationRow = {
  id: string;
  conversationType: AdminConversationType;
  title: string | null;
  communityName: string | null;
  allowsVideo: boolean;
  archivedAt: string | null;
  memberCount: number;
  memberNames: string | null;
  messageCount: number;
  lastMessagePreview: string | null;
  lastMessageAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type AdminConversationMessage = {
  id: string;
  senderId: string;
  senderName: string;
  messageType: AdminMessageType;
  body: string | null;
  createdAt: string;
  editedAt: string | null;
  deletedAt: string | null;
};

export type AdminAuditLogRow = {
  id: string;
  createdAt: string;
  actorName: string | null;
  action: string;
  entityType: string;
  entityId: string | null;
  metadata: Record<string, unknown>;
};

export const ADMIN_CONVERSATION_KIND_LABELS: Record<
  AdminConversationKind,
  string
> = {
  direct: "Privát",
  group: "Csoport",
  community: "Közösség",
  service: "Szolgáltatás",
};

export const ADMIN_MESSAGE_TYPE_LABELS: Record<AdminMessageType, string> = {
  text: "Szöveg",
  image: "Kép",
  file: "Fájl",
  link: "Link",
  system: "Rendszer",
};

export const AUDIT_ENTITY_LABELS: Record<string, string> = {
  profile: "Felhasználó",
  post: "Bejegyzés",
  comment: "Hozzászólás",
  community: "Közösség",
  conversation: "Beszélgetés",
  message: "Üzenet",
  lead: "Lead",
  practice: "Praxis",
  service: "Szolgáltatás",
  feed_ad: "Hirdetés",
  offer: "Ajánlat",
  customer: "Ügyfél",
  notification: "Értesítés",
  system: "Rendszer",
  landing_page: "Landing",
};

export const AUDIT_ACTION_LABELS: Record<string, string> = {
  "user.status_active": "Felhasználó aktiválása",
  "user.status_suspended": "Felhasználó felfüggesztése",
  "user.status_banned": "Felhasználó tiltása",
  "user.role_set": "Szerepkör módosítása",
  "admin.post_hide": "Bejegyzés elrejtése",
  "admin.post_unhide": "Bejegyzés megjelenítése",
  "admin.post_pin": "Bejegyzés rögzítése",
  "admin.post_unpin": "Bejegyzés rögzítésének feloldása",
  "admin.post_delete": "Bejegyzés törlése",
  "admin.post_restore": "Bejegyzés visszaállítása",
  "admin.message_delete": "Chatüzenet törlése",
  "admin.message_restore": "Chatüzenet visszaállítása",
  "admin.conversation_archive": "Beszélgetés archiválása",
  "admin.conversation_unarchive": "Beszélgetés visszaállítása",
  "admin.feed_ad_upsert": "Hirdetés mentése",
  "admin.feed_ad_delete": "Hirdetés törlése",
};

export function conversationKind(
  row: Pick<AdminConversationRow, "conversationType" | "allowsVideo">,
): AdminConversationKind {
  return row.allowsVideo ? "service" : row.conversationType;
}

export function conversationDisplayName(row: AdminConversationRow) {
  return (
    row.title?.trim() ||
    row.communityName?.trim() ||
    row.memberNames?.trim() ||
    "Beszélgetés"
  );
}

export function auditActionLabel(action: string) {
  return AUDIT_ACTION_LABELS[action] ?? action.replaceAll("_", " ");
}

export function auditEntityLabel(entityType: string) {
  return AUDIT_ENTITY_LABELS[entityType] ?? entityType;
}
